import { memo, useEffect } from "react";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { RepeatIcon, ReplyIcon } from "../../../../packages/icons";
import { useInViewport } from "../../../../packages/hooks";
import { helpers } from "../../../../packages/helpers";
import { Text } from "../../../../packages/ui-kit";
import { decreaseUnreadCount, setReplyMessageId } from "../../actions";
import { isMessageService } from "../../type-guards";
import { selectAuthorList } from "../../selectors";
import { ServiceMessageInfo } from "../message-kinds";
import { ChatMessageAvatar } from "./chat-message-avatar";
import { ChatMessageStatus } from "./chat-message-status";
import { ChatMessageTime } from "./chat-message-time";
import { ChatMessageContent } from "./chat-message-content";
import { getAuthor } from "./get-author";
import type { Message } from "../../types";
import styles from "./chat-message.module.scss";

const { isAuthorUser, isMessageReceived, isMessageError } = helpers.predicates;
const { getFormattedTime } = helpers.date;

type Props = {
  message: Message;
  onReadMessage: () => void;
  onResendMessage: () => void;
};

export const ChatMessage = memo(
  ({ message, onReadMessage, onResendMessage }: Props) => {
    const { isInViewport, ref } = useInViewport();
    const authorList = useAppSelector(selectAuthorList);
    const author = getAuthor(authorList, message);
    const { role } = author;
    const isUserRole = isAuthorUser(role);
    const authorName = `${author.name.first} ${author.name.last}`;
    const dispatch = useAppDispatch();

    const handleReplyMessageClick = () =>
      dispatch(setReplyMessageId(message.id));

    useEffect(() => {
      // Если сообщение от оппонента юзером непрочитанно
      // И оно появилось в видимой области
      if (isInViewport && !isUserRole && isMessageReceived(message.status)) {
        // Делаем его прочитанным
        onReadMessage();
        // Уменьшаем счетчик непрочитанных
        dispatch(decreaseUnreadCount());
      }
    }, [isInViewport]);

    if (isMessageService(message)) {
      return <ServiceMessageInfo ref={ref} message={message} />;
    }

    const messageClassNames = cn(styles.message, {
      [styles.opponent]: !isUserRole,
    });

    const status = <ChatMessageStatus status={message.status} />;

    const resendIcon = (
      <div className={styles["resend-icon"]} onClick={onResendMessage}>
        <RepeatIcon />
      </div>
    );

    const opponentName = (
      <Text
        data-test-id="chat-message-opponent-name"
        className={styles.name}
        size="xsmall"
      >
        {authorName}
      </Text>
    );

    const replyClassNames = cn(styles["reply-icon"], {
      [styles.revert]: isUserRole,
    });

    const replyIcon = (
      <div
        className={replyClassNames}
        onClick={handleReplyMessageClick}
        data-test-id="chat-message-reply-icon"
      >
        <ReplyIcon />
      </div>
    );

    return (
      <div ref={ref} data-test-id="chat-message" className={messageClassNames}>
        {!isMessageError(message.status) && replyIcon}
        <div data-test-id="chat-message-info" className={styles.info}>
          {isMessageError(message.status) && resendIcon}
          {isUserRole && status}
          <ChatMessageTime time={getFormattedTime(message.date)} />
        </div>
        {!isUserRole && <ChatMessageAvatar role={role} />}
        <div
          data-test-id="chat-message-content"
          className={styles["content-wrapper"]}
        >
          {!isUserRole && opponentName}
          <ChatMessageContent message={message} />
        </div>
      </div>
    );
  }
);
